import { useSelector } from "react-redux";
import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import React from "react";
import { store } from "../../store";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import {
  WaterDrop,
  ElectricBolt,
  Thermostat,
  DevicesOther,
  Science,
  Edit,
  Delete,
  Monitor,
  ToggleOn,
  TouchApp,
  SensorDoor,
} from "@mui/icons-material";

// Icône selon le type de device
const getTypeIcon = (deviceType) => {
  const typeName = deviceType?.name?.toLowerCase() || "";

  if (typeName.includes("water") || typeName.includes("eau")) {
    return <WaterDrop sx={{ fontSize: 20, color: "#6b9dad" }} />;
  }
  if (typeName.includes("elec") || typeName.includes("power")) {
    return <ElectricBolt sx={{ fontSize: 20, color: "#c4a574" }} />;
  }
  if (typeName.includes("temp") || typeName.includes("thermo")) {
    return <Thermostat sx={{ fontSize: 20, color: "#e07a5f" }} />;
  }
  if (typeName.includes("screen") || typeName.includes("écran") || typeName.includes("display")) {
    return <Monitor sx={{ fontSize: 20, color: "#8a9ba8" }} />;
  }
  if (typeName.includes("switch") || typeName.includes("relay")) {
    return <ToggleOn sx={{ fontSize: 20, color: "#9c88b8" }} />;
  }
  if (typeName.includes("button") || typeName.includes("bouton")) {
    return <TouchApp sx={{ fontSize: 20, color: "#d4a574" }} />;
  }
  if (typeName.includes("door") || typeName.includes("porte") || typeName.includes("contact")) {
    return <SensorDoor sx={{ fontSize: 20, color: "#7eb8c4" }} />;
  }
  return <DevicesOther sx={{ fontSize: 20, color: "#7a8a7a" }} />;
};

// Badge de statut
const getStatusBadge = (deviceBrand) => {
  if (!deviceBrand.has_decoder) {
    return (
      <Chip
        label="Sans décodeur"
        size="small"
        sx={{
          backgroundColor: "rgba(196, 165, 116, 0.2)",
          color: "#c4a574",
          fontWeight: 500,
        }}
      />
    );
  }
  return (
    <Chip
      label="Actif"
      size="small"
      sx={{
        backgroundColor: "rgba(120, 168, 130, 0.2)",
        color: "#78a882",
        fontWeight: 500,
      }}
    />
  );
};

export default function DeviceBrandsTable({ onTest, onEdit, onDelete, deviceBrands }) {
  const deviceTypes = useSelector((state) => state.deviceTypes);
  const userDB = useSelector((state) => state.userDB);

  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);

  const { t } = useTranslation("common");

  // Trouver le type de device
  const getDeviceType = (deviceTypeId) => {
    return deviceTypes?.find((dt) => dt.id === deviceTypeId) || null;
  };

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#9aa89a" }}
          >
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "name",
      label: t("common.name"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
        ),
      },
    },
    {
      name: "device_type_id",
      label: "Type",
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const deviceBrand = deviceBrands[dataIndex];
          const deviceType = getDeviceType(deviceBrand?.device_type_id);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getTypeIcon(deviceType)}
              <Typography variant="body2">
                {deviceType?.name || "—"}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "influx_measurement",
      label: "Measurement",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              backgroundColor: "rgba(120, 168, 130, 0.1)",
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            {value || "—"}
          </Typography>
        ),
      },
    },
    {
      name: "field",
      label: "Champ",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {value || "—"}
          </Typography>
        ),
      },
    },
    {
      name: "has_decoder",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const deviceBrand = deviceBrands[dataIndex];
          return getStatusBadge(deviceBrand);
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const deviceBrand = deviceBrands[dataIndex];
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Éditer">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(deviceBrand);
                  }}
                  sx={{ color: "#6b9dad" }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={deviceBrand.has_decoder ? "Tester le décodeur" : "Aucun décodeur uploadé"}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTest && onTest(deviceBrand);
                    }}
                    sx={{
                      color: deviceBrand.has_decoder ? "#78a882" : "#666",
                    }}
                    disabled={!deviceBrand.has_decoder}
                  >
                    <Science fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Supprimer">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete && onDelete(deviceBrand);
                  }}
                  sx={{ color: "#a57474" }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  const tableActions = [
    {
      name: t("common.delete_selected", {
        name: "capteurs",
        count: selectedItems.length,
      }),
      icon: "Delete",
      action: () => {
        store.dispatch({
          type: "deleteDialog/update",
          payload: {
            open: true,
            type: "device-brands",
            records: selectedItems,
          },
        });
      },
    },
  ];

  if (!deviceBrands) {
    return <div>Loading...</div>;
  }

  return (
    <DataTableBase
      columns={columns}
      data={deviceBrands}
      title="Capteurs"
      id="device-brands-list"
      name="device-brands-list"
      pageSize={10}
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      selectableRows={userDB && userDB.admin ? "multiple" : "none"}
      actions={tableActions}
      sortOrder="name"
      onRowClick={(rowData, rowMeta) => {
        const deviceBrand = deviceBrands[rowMeta.dataIndex];
        onEdit && onEdit(deviceBrand);
      }}
    />
  );
}
